/**
 * C-17 注文の手入力（FR-23・T-D7）＝電話・FAX・メールで受けた注文を卸スタッフが起票する。
 *
 * **なぜ M1 に要るか**：AI 下書きを凍結している間（D-59）、FAX とメールは「卸が原本を見て手入力」で
 * 受ける。backend は前から実装済みだったが画面が無く、受注箱の FAX ボタンだけが残っていた＝
 * 「取り込みました」と出るのに注文が現れない、という一番事故る状態だった。
 *
 * **AI も接続も要らない**＝得意先を選んで明細を入れるだけ。単価は単価3層で自動解決され、
 * 要確認フラグは立たない（人が直接入れた値だから）＝そのまま承認できる。
 */

import { console_, type Customer, type Product, type Site } from "@kenzai/api-client";
import { Card, ErrorNote, Money, PageHeader, Spinner } from "@kenzai/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { hintStyle, inputStyle, labelStyle } from "./formStyles";
import { MOBILE_TABBAR_HEIGHT, useIsMobile } from "../useIsMobile";

/** 手入力で選べるチャネル（`web` は職人アプリ専用＝ここからは作らせない・backend と同じ値域）。 */
const CHANNELS: { value: "tel" | "fax" | "mail"; label: string }[] = [
  { value: "tel", label: "電話" },
  { value: "fax", label: "FAX" },
  { value: "mail", label: "メール" },
];

type Line = { key: number; productId: number | null; qty: string };

export function OrderNew() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<"tel" | "fax" | "mail">("tel");
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [siteId, setSiteId] = useState<number | null>(null);
  const [desiredDate, setDesiredDate] = useState("");
  const [binId, setBinId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>([{ key: 1, productId: null, qty: "" }]);
  // 二重クリックで注文が2件できないよう、この画面を開いた時に1つだけ鍵を作って持ち回る（05 §1.4）
  const [idempotencyKey] = useState(() => `manual-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  // 取引先・商品は件数が多くなりうる（卸の SKU は数百〜）＝**検索して絞る**。
  // 一覧APIの limit 上限は 100（05 §1.4）＝ここを超える値を送ると 422 で選択肢が空になる。
  const [customerQuery, setCustomerQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const customers = useQuery({
    queryKey: ["customers", "manual", customerQuery],
    queryFn: () =>
      console_.customers(`?limit=100${customerQuery ? `&q=${encodeURIComponent(customerQuery)}` : ""}`),
  });
  const products = useQuery({
    queryKey: ["products", "manual", productQuery],
    queryFn: () =>
      console_.products(`?limit=100${productQuery ? `&q=${encodeURIComponent(productQuery)}` : ""}`),
  });
  const bins = useQuery({ queryKey: ["bins"], queryFn: () => console_.bins() });
  const sites = useQuery({
    queryKey: ["sites", customerId],
    queryFn: () => console_.sites(customerId as number),
    enabled: customerId !== null,
  });

  const productById = useMemo(() => {
    const m = new Map<number, Product>();
    for (const p of products.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [products.data]);

  // ★**検索で選択肢から消えた選択は外す**（レビュー指摘）。
  // 検索し直すと `<select className="kz-select">` の option が入れ替わるので、選んだ相手が一覧から落ちると
  // 画面は空欄に見えるのに state には id が残る＝**画面に出ていない取引先・商品のまま登録**できてしまう。
  // AI 凍結中の FAX・電話は**この画面が唯一の入口**なので、ここは「見えているものだけ送る」に倒す。
  useEffect(() => {
    if (customers.isLoading || customerId === null) return;
    if (!(customers.data?.items ?? []).some((c: Customer) => c.id === customerId)) {
      setCustomerId(null);
      setSiteId(null);
    }
  }, [customers.data, customers.isLoading, customerId]);

  useEffect(() => {
    if (products.isLoading) return;
    setLines((prev) =>
      prev.map((l) =>
        l.productId !== null && !productById.has(l.productId) ? { ...l, productId: null } : l,
      ),
    );
  }, [productById, products.isLoading]);

  const create = useMutation({
    mutationFn: () =>
      console_.createOrder(
        {
          customer_id: customerId as number,
          channel,
          site_id: siteId,
          desired_date: desiredDate || null,
          bin_id: binId,
          note: note.trim() === "" ? null : note.trim(),
          // **活性判定と同じ述語**を使う（別々だと qty=0/-3 の行が payload にだけ混ざり、
          // 画面はどの行が悪いか分からない 400 を見せることになる＝レビュー指摘）
          items: filled.map((l) => ({ product_id: l.productId as number, qty: l.qty })),
        },
        idempotencyKey,
      ),
    onSuccess: (order) => navigate(`/orders/${order.order.id}`),
  });

  const filled = lines.filter((l) => l.productId !== null && Number(l.qty) > 0);
  const canSubmit = customerId !== null && filled.length > 0 && !create.isPending;
  // 入力されているのに送られない行（数量が 0 以下・空）＝黙って落とさず、その場で理由を出す
  const ignored = lines.filter((l) => l.productId !== null && !(Number(l.qty) > 0)).length;

  // 合計の目安（正は受注詳細＝サーバが単価3層で解決した金額）。ここは入力中の目安として出す。
  const roughTotal = filled.reduce((sum, l) => {
    const p = productById.get(l.productId as number);
    return sum + (p?.list_price ? Number(p.list_price) * Number(l.qty) : 0);
  }, 0);

  return (
    <>
      <PageHeader
        title="注文の手入力"
        subtitle="電話・FAX・メールで受けた注文を入力します。単価は取引先の設定から自動で入ります。"
      />
      {create.isError && <ErrorNote error={create.error} />}

      <Card>
        <div style={{ display: "grid", gap: "var(--kz-space-4)", maxWidth: 720 }}>
          <label style={labelStyle}>
            受けた方法
            <select className="kz-select"
              data-testid="manual-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as "tel" | "fax" | "mail")}
              style={inputStyle}
            >
              {CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            取引先
            <input
              type="search"
              data-testid="manual-customer-search"
              placeholder="取引先を検索（Enter）"
              onKeyDown={(e) => e.key === "Enter" && setCustomerQuery((e.target as HTMLInputElement).value)}
              style={{ ...inputStyle, marginBottom: "var(--kz-space-1)" }}
            />
            {customers.isLoading ? (
              <Spinner />
            ) : (
              <select className="kz-select"
                data-testid="manual-customer"
                value={customerId ?? ""}
                onChange={(e) => {
                  setCustomerId(e.target.value ? Number(e.target.value) : null);
                  setSiteId(null);
                }}
                style={inputStyle}
              >
                <option value="">選んでください</option>
                {(customers.data?.items ?? []).map((c: Customer) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label style={labelStyle}>
            現場（任意）
            <select className="kz-select"
              data-testid="manual-site"
              value={siteId ?? ""}
              disabled={customerId === null}
              onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : null)}
              style={inputStyle}
            >
              <option value="">指定しない</option>
              {(sites.data ?? []).map((s: Site) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>

          <div style={{ display: "flex", gap: "var(--kz-space-4)", flexWrap: "wrap" }}>
            <label style={{ ...labelStyle, flex: "1 1 220px" }}>
              納品希望日（任意）
              <input
                type="date"
                data-testid="manual-date"
                value={desiredDate}
                onChange={(e) => setDesiredDate(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={{ ...labelStyle, flex: "1 1 220px" }}>
              配送便（任意）
              <select className="kz-select"
                data-testid="manual-bin"
                value={binId ?? ""}
                onChange={(e) => setBinId(e.target.value ? Number(e.target.value) : null)}
                style={inputStyle}
              >
                <option value="">指定しない</option>
                {(bins.data ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}（{b.cutoff_time.slice(0, 5)} 締め）
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <div style={{ ...labelStyle, marginBottom: "var(--kz-space-2)" }}>明細</div>
            <input
              type="search"
              data-testid="manual-product-search"
              placeholder="商品を検索（Enter）"
              onKeyDown={(e) => e.key === "Enter" && setProductQuery((e.target as HTMLInputElement).value)}
              style={{ ...inputStyle, marginBottom: "var(--kz-space-2)", width: 260 }}
            />
            <div style={{ display: "grid", gap: "var(--kz-space-2)" }}>
              {lines.map((l, i) => (
                // ★スマホは折り返す＝[商品 320px][数量 120px][単位 40px][削除] は 390px に入らない。
                //   商品を1行使い、数量・単位・削除を次の行に並べる（`flexWrap` だけで成立する）。
                <div
                  key={l.key}
                  style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "center", flexWrap: "wrap" }}
                >
                  <select className="kz-select"
                    data-testid={`manual-product-${i}`}
                    value={l.productId ?? ""}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((x) =>
                          x.key === l.key
                            ? { ...x, productId: e.target.value ? Number(e.target.value) : null }
                            : x,
                        ),
                      )
                    }
                    style={{ ...inputStyle, flex: isMobile ? "1 1 100%" : "1 1 320px" }}
                  >
                    <option value="">商品を選んでください</option>
                    {(products.data?.items ?? [])
                      .filter((p: Product) => p.status === "active")
                      .map((p: Product) => (
                        <option key={p.id} value={p.id}>
                          {p.display_no ? `${p.display_no} ` : ""}
                          {p.name}
                          {p.spec ? `（${p.spec}）` : ""}
                        </option>
                      ))}
                  </select>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    data-testid={`manual-qty-${i}`}
                    placeholder="数量"
                    value={l.qty}
                    onChange={(e) =>
                      setLines((prev) =>
                        prev.map((x) => (x.key === l.key ? { ...x, qty: e.target.value } : x)),
                      )
                    }
                    style={{ ...inputStyle, width: 120, flex: isMobile ? "1 1 auto" : undefined }}
                  />
                  <span style={{ ...hintStyle, width: 40 }}>
                    {l.productId ? (productById.get(l.productId)?.unit ?? "") : ""}
                  </span>
                  {lines.length > 1 && (
                    <button
                      type="button"
                      data-testid={`manual-remove-${i}`}
                      onClick={() => setLines((prev) => prev.filter((x) => x.key !== l.key))}
                      className="kz-btn"
                    >
                      削除
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              type="button"
              data-testid="manual-add-line"
              onClick={() =>
                setLines((prev) => [...prev, { key: Math.max(...prev.map((p) => p.key)) + 1, productId: null, qty: "" }])
              }
              className="kz-btn"
              style={{ marginTop: "var(--kz-space-2)" }}
            >
              明細を追加
            </button>
          </div>

          <label style={labelStyle}>
            備考（任意）
            <input
              data-testid="manual-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              style={inputStyle}
            />
            <span style={hintStyle}>電話で聞いた指定などを残せます。連絡先は取引先マスタで管理してください。</span>
          </label>

          {/* ★スマホは合計と登録を**下部に固定**（D-68）＝明細を足すほど下へ伸びるので、
              固定しないと「登録」を押すたびに最下部までスクロールすることになる。
              下部タブと同じ場所なので、タブの高さぶん持ち上げ z-index もタブ（20）より低くする。 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--kz-space-4)",
              flexWrap: "wrap",
              ...(isMobile
                ? {
                    position: "sticky" as const,
                    bottom: MOBILE_TABBAR_HEIGHT,
                    zIndex: 15,
                    background: "var(--kz-color-surface)",
                    borderTop: "1px solid var(--kz-color-border)",
                    padding: "var(--kz-space-3) 0",
                  }
                : {}),
            }}
          >
            <div style={hintStyle}>
              {ignored > 0 && (
                <div data-testid="ignored-lines" style={{ color: "var(--kz-status-warn)" }}>
                  数量が入っていない行が {ignored} 行あります。この行は登録されません。
                </div>
              )}
              金額の目安（定価ベース）：<Money value={roughTotal > 0 ? String(roughTotal) : null} />
              <br />
              取引先の掛率を適用した正しい金額は、登録後の受注詳細でご確認いただけます。
            </div>
            <button
              type="button"
              data-testid="manual-submit"
              disabled={!canSubmit}
              onClick={() => create.mutate()}
              className="kz-btn kz-btn--primary"
              style={{
                marginLeft: "auto",
                ...(isMobile ? { width: "100%", marginLeft: 0 } : {}),
              }}
            >
              この内容で受注に登録
            </button>
          </div>
        </div>
      </Card>
    </>
  );
}
